import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloModal } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { tokens } from '@synergy/theme-palo';

import { SkippedInvoiceResponse } from '../invoice-api';

const c = tokens.color;

export interface PaloSkippedInvoicesDialogProps {
    skipped: SkippedInvoiceResponse[];
    onClose: () => void;
    /**
     * Set where this is the only receipt there was — the open mask, not a
     * batch. There is no rest of the pile to report on then.
     */
    single?: boolean;
}

/**
 * What the batch left behind (#2136).
 *
 * The batch no longer stops at the first invoice it cannot complete, which
 * means the run can end half-done. That is only an improvement if the half is
 * named: this lists every skipped invoice with its reason, so the work left
 * over is a list to walk rather than a number to wonder about.
 */
export const PaloSkippedInvoicesDialog = ({
    skipped,
    onClose,
    single,
}: PaloSkippedInvoicesDialogProps): ReactElement => (
    <PaloModal
        isVisible={skipped.length > 0}
        handleClose={onClose}
        title={<FormattedMessage id="app.invoices.complete.skippedTitle" values={{ count: skipped.length }} />}
        width="sm"
        themeContent={true}
        actionChildren={
            <Box
                component="button"
                type="button"
                onClick={onClose}
                sx={{
                    height: 30,
                    padding: '0 13px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    color: c.ink2,
                    backgroundColor: c.surface,
                    border: `1px solid ${c.lineHard}`,
                    borderRadius: tokens.radius.small + 'px',
                }}
            >
                <FormattedMessage id="app.common.close" />
            </Box>
        }
    >
        <Box sx={{ fontSize: '13px', lineHeight: '20px', color: c.ink, paddingBottom: '9px' }}>
            <FormattedMessage
                id={single ? 'app.invoices.complete.skippedIntro.single' : 'app.invoices.complete.skippedIntro'}
            />
        </Box>
        {skipped.map(entry => (
            <Box
                key={entry.id}
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '11px',
                    padding: '5px 0',
                    borderBottom: `1px solid ${c.lineSoft}`,
                    '&:last-of-type': { borderBottom: 0 },
                    fontSize: '12.5px',
                }}
            >
                <Box component="span" sx={{ flex: '1 1 0', fontVariantNumeric: 'tabular-nums', color: c.ink }}>
                    {entry.number ?? `#${entry.id}`}
                </Box>
                <Box component="span" sx={{ color: c.ink2, whiteSpace: 'nowrap' }}>
                    <FormattedMessage id={`app.invoices.complete.skipped.${entry.reason}`} />
                </Box>
            </Box>
        ))}
    </PaloModal>
);
